import React from 'react';
import { User } from '../../types';
import { getCurrentRank, getRankProgress } from '../../utils/gameData';

interface UserProfileProps {
  user: User;
}

const UserProfile: React.FC<UserProfileProps> = ({ user }) => {
  const formatMoney = (money: number) => {
    return `$${money.toLocaleString()}`;
  };

  const getRankName = (rankId: number) => {
    const rank = getCurrentRank(rankId);
    return rank?.name || 'Unknown';
  };

  const getCarName = (carId: number | null): string => {
    const carTypes = [
      'None', 'Honda Civic', 'Toyota Camry', 'BMW 3 Series', 
      'Mercedes C-Class', 'Porsche 911', 'Ferrari F40', 'Lamborghini Countach'
    ];
    return carId ? carTypes[carId] || 'Unknown Car' : 'No Car';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'alive': return 'var(--success-color)';
      case 'dead': return 'var(--error-color)';
      case 'jailed': return 'var(--warning-color)';
      default: return 'var(--text-light)';
    }
  };

  // Calculate level from experience
  const calculateLevel = (experience: number) => {
    return Math.floor(experience / 100) + 1;
  };

  const getProfileImage = (username: string) => {
    // Generate a consistent color based on username
    const colors = [
      '#ff6b35', '#f7931e', '#ffd23f', '#4caf50', '#2196f3', 
      '#9c27b0', '#e91e63', '#ff5722', '#795548', '#607d8b'
    ];
    const colorIndex = username.charCodeAt(0) % colors.length;
    const backgroundColor = colors[colorIndex];
    
    return {
      backgroundColor,
      initials: username.substring(0, 2).toUpperCase()
    };
  };

  return (
    <div className="user-profile">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar">
          <div 
            className="profile-image"
            style={{ backgroundColor: getProfileImage(user.username).backgroundColor }}
          >
            {getProfileImage(user.username).initials}
          </div>
        </div>
        <div className="profile-info">
          <h3 className="profile-name">{user.username}</h3>
          <p className="profile-rank">{getRankName(user.rankId)}</p>
          <p className="profile-money">{formatMoney(user.money)}</p>
        </div>
      </div>

      {/* Health Bar */}
      <div className="profile-health">
        <div className="health-label">
          <span>Health</span>
          <span>{user.health}%</span>
        </div>
        <div className="health-bar">
          <div 
            className="health-fill" 
            style={{ width: `${user.health}%` }}
          />
        </div>
      </div>

      {/* Rank Progress Bar */}
      <div className="profile-rank-progress">
        <div className="rank-progress-label">
          <span>Rank Progress</span>
          <span>{getCurrentRank(user.rankId)?.name || 'Unknown'}</span>
        </div>
        <div className="rank-progress-bar">
          <div 
            className="rank-progress-fill" 
            style={{ width: `${getRankProgress(user.experience, user.rankId).progress}%` }}
          />
        </div>
        <div className="rank-progress-stats">
          <span>{user.experience.toLocaleString()} XP</span>
          {getRankProgress(user.experience, user.rankId).nextRankExp > 0 && (
            <span>Next: {getRankProgress(user.experience, user.rankId).nextRankExp.toLocaleString()} XP</span>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="profile-stats">
        <div className="stat-item">
          <span className="stat-label">Location</span>
          <span className="stat-value">{user.locationName}</span>
        </div>
        
        <div className="stat-item">
          <span className="stat-label">Car</span>
          <span className="stat-value">{getCarName(user.carId)}</span>
        </div>
        
        {user.carId && (
          <div className="stat-item">
            <span className="stat-label">Car Health</span>
            <span className="stat-value">{user.carHealth}%</span>
          </div>
        )}
        
        <div className="stat-item">
          <span className="stat-label">Rank Position</span>
          <span className="stat-value">#{user.rankId}</span>
        </div>
        
        <div className="stat-item">
          <span className="stat-label">Experience</span>
          <span className="stat-value">{user.experience.toLocaleString()} XP</span>
        </div>
        
        <div className="stat-item">
          <span className="stat-label">Level</span>
          <span className="stat-value">{calculateLevel(user.experience)}</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="profile-actions">
        <button className="btn btn-primary w-100 mb-2">
          View Profile
        </button>
        <button className="btn btn-secondary w-100">
          Settings
        </button>
      </div>
    </div>
  );
};

export default UserProfile;
